import React, { memo, useMemo, useCallback } from 'react';
import stringUtils from '../../util/stringUtils';
import IconMC from './IconMC';

/**
 * AvatarMC - Avatar Component (Atom Level)
 *
 * Componente de avatar versátil que puede mostrar imágenes, iconos o iniciales.
 * Sigue los principios de Atomic Design como componente atom-level con alta reutilización.
 * Implementa detección inteligente del tipo de contenido y personalización completa via className props.
 *
 * CASOS DE USO:
 * - Perfiles de usuario con foto
 * - Avatares con iconos representativos
 * - Iniciales como fallback cuando no hay imagen
 * - Representación visual de entidades (clientes, agentes, bots, etc.)
 *
 * FUNCIONALIDADES:
 * - Detección automática de tipo de imagen (URL vs nombre de icono)
 * - Fallback a iniciales cuando no hay imagen
 * - Personalización completa via className props
 * - Responsive y accesible por defecto
 * - Integración con IconMC para iconos
 *
 * OPTIMIZACIONES DE PERFORMANCE:
 * - React.memo para prevenir re-renders innecesarios
 * - useMemo para cálculos costosos y objetos de configuración
 * - useCallback para funciones internas cuando sea necesario
 * - Memoización de lógica de detección de tipo de imagen
 * - Optimización de concatenación de clases CSS
 *
 * DETECCIÓN DE TIPO DE IMAGEN:
 * - String que NO empieza con 'http', '/' y NO contiene '.' → IconMC
 * - String que empieza con 'http', '/' o contiene '.' → <img>
 * - Fallback a iniciales si no hay imagen
 *
 * @param {Object} props - Propiedades del componente
 * @param {string|React.Component} [props.image] - URL de imagen, nombre de icono, o componente de imagen
 * @param {string} [props.fullName] - Nombre completo para generar iniciales (fallback cuando no hay imagen)
 * @param {string} [props.className=''] - Clases CSS adicionales para el wrapper principal
 * @param {string} [props.avatarClassName=''] - Clases CSS para el contenedor circular del avatar
 * @param {string} [props.imageClassName=''] - Clases CSS específicas para el elemento <img>
 * @param {string} [props.iconClassName=''] - Clases CSS específicas para el componente IconMC
 * @param {string} [props.initialsClassName=''] - Clases CSS específicas para el texto de iniciales
 *
 * @returns {React.ReactElement|null} Elemento JSX del avatar o null si no hay contenido que mostrar
 *
 * @example
 * // Avatar con imagen (URL completa)
 * <AvatarMC 
 *   image="https://example.com/avatar.jpg"
 *   fullName="Juan Pérez"
 *   className="tw-mx-4"
 *   imageClassName="tw-border-2 tw-border-blue-500"
 * />
 *
 * @example
 * // Avatar con icono (string sin URL)
 * <AvatarMC 
 *   image="user"
 *   fullName="Usuario Anónimo"
 *   avatarClassName="tw-bg-green-100"
 *   iconClassName="tw-text-green-600 tw-w-6 tw-h-6"
 * />
 *
 * @example
 * // Avatar con iniciales (sin imagen)
 * <AvatarMC 
 *   fullName="María González"
 *   avatarClassName="tw-bg-purple-100 tw-size-12"
 *   initialsClassName="tw-text-purple-700 tw-font-bold"
 * />
 *
 * @example
 * // Avatar personalizado para diferentes tamaños
 * <AvatarMC 
 *   image="star"
 *   fullName="Cliente VIP"
 *   className="tw-cursor-pointer tw-transition-transform hover:tw-scale-110"
 *   avatarClassName="tw-bg-gradient-to-r tw-from-yellow-400 tw-to-yellow-600 tw-size-16 tw-shadow-lg"
 *   iconClassName="tw-text-white tw-w-8 tw-h-8"
 * />
 *
 * @example
 * // Avatar con rutas locales de imagen
 * <AvatarMC 
 *   image="/images/avatars/default.png"
 *   fullName="Usuario Local"
 *   imageClassName="tw-size-14 tw-ring-2 tw-ring-blue-300"
 * />
 *
 * @example
 * // Avatar optimizado para listas grandes (evita re-renders)
 * const CustomerList = memo(() => {
 *   const customers = useCustomers();
 *   return customers.map(customer => (
 *     <AvatarMC 
 *       key={customer.id}
 *       image={customer.avatar || customer.initials}
 *       fullName={customer.fullName}
 *       className="tw-mr-2"
 *     />
 *   ));
 * });
 *
 * @performance
 * - Optimizado con React.memo para prevenir re-renders innecesarios
 * - useMemo para cálculos costosos (detección de tipo, clases CSS)
 * - Memoización de avatarContent para evitar recreación de JSX
 * - Ideal para uso en listas grandes o componentes que se actualizan frecuentemente
 */

const AvatarMC = memo(({ 
  image, 
  fullName, 
  className = '', 
  avatarClassName = '', 
  imageClassName = '', 
  iconClassName = '', 
  initialsClassName = '' 
}) => {
  /**
   * Detección optimizada del tipo de imagen
   * Memoizada para evitar cálculos repetitivos en re-renders
   * 
   * @returns {boolean} true si es icono, false si es imagen/URL
   */
  const isIcon = useMemo(() => {
    if (!image || typeof image !== 'string') return false;
    return !image.startsWith('http') && 
           !image.startsWith('/') && 
           !image.includes('.');
  }, [image]);

  /**
   * Clases CSS optimizadas y memoizadas
   * Previene recreación de strings en cada render
   */
  const cssClasses = useMemo(() => ({
    wrapper: `tw-wrapper ${className}`,
    avatarContainer: `tw-inline-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-sky-50 ${avatarClassName}`,
    image: `tw-inline-block tw-size-10 tw-rounded-full ${imageClassName}`,
    icon: `tw-text-blue-500 tw-w-5 tw-h-5 ${iconClassName}`,
    initials: `tw-text-sm tw-text-blue-500 ${initialsClassName}`
  }), [className, avatarClassName, imageClassName, iconClassName, initialsClassName]);

  /**
   * Función interna para determinar qué tipo de avatar mostrar
   * Optimizada con useMemo para evitar cálculos repetitivos
   * 
   * LÓGICA DE DECISIÓN:
   * 1. Si no hay image ni fullName → retorna null (no renderiza nada)
   * 2. Si hay image:
   *    - String sin http/slash/punto → IconMC (ej: "user", "star")
   *    - String con http/slash/punto → <img> (ej: "https://...", "/images/...", "avatar.jpg")
   * 3. Si no hay image pero sí fullName → Iniciales con stringUtils
   * 
   * DETECCIÓN DE TIPO DE IMAGEN:
   * - Iconos: strings simples como "user", "star", "settings"
   * - URLs: "https://example.com/avatar.jpg", "http://...", "/images/avatar.png"
   * - Archivos: "avatar.jpg", "profile.png" (contienen punto)
   * 
   * @returns {React.ReactElement|null} JSX del avatar o null
   * 
   * @example
   * // Casos que devuelven IconMC:
   * // image = "user" → <IconMC name="user" />
   * // image = "star" → <IconMC name="star" />
   * 
   * @example
   * // Casos que devuelven <img>:
   * // image = "https://example.com/avatar.jpg" → <img src="..." />
   * // image = "/images/avatar.png" → <img src="..." />
   * // image = "avatar.jpg" → <img src="..." />
   * 
   * @example
   * // Casos que devuelven iniciales:
   * // image = null, fullName = "Juan Pérez" → <span>JP</span>
   */
  const avatarContent = useMemo(() => {
    // Validación: si no hay contenido que mostrar, no renderizar nada
    if (!image && !(fullName || '').trim()) return null;
    
    // Caso 1: Hay imagen - determinar si es icono o imagen real
    if (image) {
      return isIcon
        ? (
            // Renderizar como icono con IconMC
            <span className={cssClasses.avatarContainer}>
              <IconMC name={image} className={cssClasses.icon} />
            </span>
          )
        : (
            // Renderizar como imagen con <img>
            <img alt={fullName} src={image} className={cssClasses.image} />
          );
    }
    
    // Caso 2: No hay imagen pero sí fullName - mostrar iniciales
    return (
      <span className={cssClasses.avatarContainer}>
        <span className={cssClasses.initials}>
          {stringUtils.getInitialsFromFullName(fullName)}
        </span>
      </span>
    );
  }, [image, fullName, isIcon, cssClasses]);
  
  // Renderizar el wrapper con las clases personalizadas y el contenido del avatar optimizado
  return <div className={cssClasses.wrapper}>{avatarContent}</div>;
});

// Asignar displayName para debugging y React DevTools
AvatarMC.displayName = 'AvatarMC';

export default AvatarMC;
